// Videos and posts that should play in the article. A paragraph holding nothing but
// a YouTube or Instagram link becomes that player, and Instagram's own embed (a
// quote block that needs its script, which the sanitizer removes) becomes its
// iframe. Runs at render on both clients; Android mirrors it in Rules.kt and both
// pass packages/core/vectors/embeds.json.

const YT = /^https?:\/\/(?:www\.|m\.)?(?:youtube\.com\/(?:watch\?(?:[^"#]*&)?v=|shorts\/|live\/)|youtu\.be\/)([\w-]{11})/i;
const IG = /^https?:\/\/(?:www\.)?instagram\.com\/(p|reel|tv)\/([\w-]+)/i;

/**
 * Which picture an image address shows, whatever size or server was asked for: its folder and
 * file name, without the host (a CDN copy is the same photo), query, extension or size
 * marks (-1024x576, -scaled, .width-1920, .fill-1280x720, Pinkbike's p4pb/p5pb/p6pb). Two
 * images with the same key in one article are one photo shown twice; two files of the same
 * name in different folders are two photos.
 */
export function imageKey(src: string): string {
  const path = (src.split(/[?#]/)[0] ?? "")
    .replace(/^https?:\/\/i\d\.wp\.com\/[^/]+/i, "") // Photon serves another site's file under that site's name
    .replace(/^https?:\/\/[^/]+/i, "")
    .replace(/\/p\dpb(\d+)\/p\dpb\1\./i, "/pb$1/pb$1.");
  const cut = path.lastIndexOf("/");
  const name = path.slice(cut + 1)
    .replace(/\.(?:width|height|fill|max|min)-[\w-]+(?=\.)/gi, "")
    .replace(/-(?:\d+x\d+|scaled|e\d{10,})(?=\.)/gi, "")
    .replace(/\.(?:jpe?g|png|webp|gif|avif)$/i, "");
  return (path.slice(0, cut + 1) + name).toLowerCase();
}

/** The player address for a YouTube or Instagram post link, or null. */
export function embedFor(url: string): string | null {
  const y = YT.exec(url);
  if (y) return `https://www.youtube-nocookie.com/embed/${y[1]}`;
  const g = IG.exec(url);
  if (g) return `https://www.instagram.com/${g[1] === "tv" ? "p" : g[1]}/${g[2]}/embed/`;
  return null;
}

const frame = (src: string) =>
  `<iframe src="${src}" allowfullscreen loading="lazy" title="${src.includes("instagram.com") ? "Instagram post" : "YouTube video"}"></iframe>`;

export function embedLinks(html: string): string {
  return html
    // Instagram's embed markup: keep the post, as its own player
    .replace(/<blockquote\b[^>]*instagram-media[^>]*>[\s\S]*?<\/blockquote>/gi, (block) => {
      const href = /href="(https?:\/\/(?:www\.)?instagram\.com\/(?:p|reel|tv)\/[^"]+)"/i.exec(block)?.[1];
      const src = href && embedFor(href.replace(/&amp;/g, "&"));
      return src ? frame(src) : block;
    })
    // a paragraph that is only a link to a video or post
    .replace(/<p\b[^>]*>\s*<a\b[^>]*href="([^"]+)"[^>]*>[^<]*<\/a>\s*<\/p>/gi, (para, href: string) => {
      const src = embedFor(href.replace(/&amp;/g, "&"));
      return src ? frame(src) : para;
    });
}

/** How wide an article's photos show at most, in CSS pixels: the reader's widest column. */
export const PHOTO_SIZES = "(max-width: 1000px) 100vw, 1000px";

const PINKBIKE = /^(https:\/\/ep\d\.pinkbike\.org\/)p\dpb(\d+)\/p\dpb\2\.jpg$/i;
const RESIZABLE = /^https:\/\/(?:platform\.theverge\.com|i\d\.wp\.com)\//i;

/**
 * Sizes for a photo that comes at one large size from a server that makes others: Pinkbike
 * (p4 800, p5 1024, p6 1600 pixels wide) and image services that take a width (The Verge's,
 * WordPress's Photon). The browser then picks for the screen: a phone takes about 1600, a
 * laptop about 1000, not The Verge's 2400 (four times the bytes). Null when there is nothing
 * to choose, or the address asks for a crop of its own. Android mirrors it in Rules.kt and
 * both pass packages/core/vectors/embeds.json.
 */
export function photoSizes(src: string): { src: string; srcset: string } | null {
  const pb = PINKBIKE.exec(src);
  if (pb) {
    const at = (n: number) => `${pb[1]}p${n}pb${pb[2]}/p${n}pb${pb[2]}.jpg`;
    return { src: at(5), srcset: `${at(4)} 800w, ${at(5)} 1024w, ${at(6)} 1600w` };
  }
  if (!RESIZABLE.test(src) || /[?&](?:resize|fit)=/.test(src)) return null;
  const at = (w: number) => /[?&]w=\d+/.test(src) ? src.replace(/([?&]w=)\d+/, `$1${w}`) : `${src}${src.includes("?") ? "&" : "?"}w=${w}`;
  return { src: at(1200), srcset: [640, 960, 1200, 1600, 2000].map((w) => `${at(w)} ${w}w`).join(", ") };
}
